import { CreateProduct } from '@frontend/product/types';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useScreen } from '../useScreen';

export type ProductTranslationSectionKey = 'name' | 'description' | 'user_info';
type ProductTranslationModifiedSections = Record<ProductTranslationSectionKey, boolean>;

const NAME_TRANSLATION_FIELDS = ['name_nl_be', 'name_en_us', 'name_fr_fr', 'name_de_de'] as const;
const DESCRIPTION_TRANSLATION_FIELDS = ['description_nl_be', 'description_en_us', 'description_fr_fr', 'description_de_de'] as const;
const USER_INFO_TRANSLATION_FIELDS = ['user_info_nl_be', 'user_info_en_us', 'user_info_fr_fr', 'user_info_de_de'] as const;
const DEFAULT_MODIFIED_SECTIONS: ProductTranslationModifiedSections = {
    name: false,
    description: false,
    user_info: false
};

const initializeTranslations = (product: CreateProduct | undefined, modifiedSections: ProductTranslationModifiedSections): CreateProduct => {
    const nextProduct = { ...(product ?? {}) };

    if (!modifiedSections.name) {
        NAME_TRANSLATION_FIELDS.forEach((field) => {
            nextProduct[field] = nextProduct.name || '';
        });
    }
    if (!modifiedSections.description) {
        DESCRIPTION_TRANSLATION_FIELDS.forEach((field) => {
            nextProduct[field] = nextProduct.description || '';
        });
    }
    if (!modifiedSections.user_info) {
        USER_INFO_TRANSLATION_FIELDS.forEach((field) => {
            nextProduct[field] = nextProduct.user_info || '';
        });
    }

    return nextProduct;
};

const useEnterProductTranslations = (props: { wizard_id: string }) => {
    const { store, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const [modifiedSections, setModifiedSections] = useState<ProductTranslationModifiedSections>({
        ...DEFAULT_MODIFIED_SECTIONS,
        ...(store?.data.productTranslationModifiedSections ?? {})
    });
    const [product, setProduct] = useState<CreateProduct>(() => initializeTranslations(store?.data.createProduct, modifiedSections));

    const previousName = useRef(product.name ?? '');
    const previousDescription = useRef(product.description ?? '');
    const previousUserInfo = useRef(product.user_info ?? '');

    useEffect(() => {
        const currentName = product.name ?? '';
        const currentDescription = product.description ?? '';
        const currentUserInfo = product.user_info ?? '';

        if (currentName !== previousName.current && !modifiedSections.name) {
            setProduct((prev) => ({
                ...prev,
                name_nl_be: currentName,
                name_en_us: currentName,
                name_fr_fr: currentName,
                name_de_de: currentName
            }));
        }
        if (currentDescription !== previousDescription.current && !modifiedSections.description) {
            setProduct((prev) => ({
                ...prev,
                description_nl_be: currentDescription,
                description_en_us: currentDescription,
                description_fr_fr: currentDescription,
                description_de_de: currentDescription
            }));
        }
        if (currentUserInfo !== previousUserInfo.current && !modifiedSections.user_info) {
            setProduct((prev) => ({
                ...prev,
                user_info_nl_be: currentUserInfo,
                user_info_en_us: currentUserInfo,
                user_info_fr_fr: currentUserInfo,
                user_info_de_de: currentUserInfo
            }));
        }

        previousName.current = currentName;
        previousDescription.current = currentDescription;
        previousUserInfo.current = currentUserInfo;
    }, [product.name, product.description, product.user_info, modifiedSections]);

    const translationSections = useMemo(
        () => [
            {
                key: 'name' as const,
                fields: NAME_TRANSLATION_FIELDS
            },
            {
                key: 'description' as const,
                fields: DESCRIPTION_TRANSLATION_FIELDS
            },
            {
                key: 'user_info' as const,
                fields: USER_INFO_TRANSLATION_FIELDS
            }
        ],
        []
    );

    const onTranslationChange = (section: ProductTranslationSectionKey, field: keyof CreateProduct, value: string) => {
        const nextModifiedSections = {
            ...modifiedSections,
            [section]: true
        };
        const nextProduct = {
            ...product,
            [field]: value || null
        };

        setModifiedSections(nextModifiedSections);
        setProduct(nextProduct);
        updateWizardData({
            createProduct: nextProduct,
            productTranslationModifiedSections: nextModifiedSections
        });
    };

    const onSubmit = () => {
        updateWizardData({
            createProduct: product,
            productTranslationModifiedSections: modifiedSections
        });
        navigateToNextStep();
    };

    return {
        product,
        translationSections,
        onTranslationChange,
        onSubmit
    };
};

export default useEnterProductTranslations;
